import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Discussion } from "../../../api/content/discussionsApi";
import type { Review } from "../../../api/content/reviewDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import DiscussionPanel from "../../discussion/DiscussionPanel";
import { compactIri } from "../../ontology/viewer/types";

interface TermDiscussionsPanelProps {
  review: Review;
  /** The review's discussion: every subject it gathers, terms included (reviews.md R-7). */
  discussion: Discussion | null;
  onChanged: () => void;
}

/**
 * Comments and issues on the terms the change touches, so a comment written on a class is
 * seen in its review (FR-VERSION-6). Only terms that have a discussion are listed; each
 * opens the term as proposed, at the change set's head.
 */
export default function TermDiscussionsPanel({ review, discussion, onChanged }: TermDiscussionsPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  if (discussion === null) {
    return null;
  }
  const terms = [...new Set([...discussion.comments, ...discussion.issues].filter((entry) => entry.subject.kind === "ontology_term").map((entry) => entry.subject.value))].sort();
  if (terms.length === 0) {
    return null;
  }
  return (
    <section className="app-card space-y-3 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-base font-semibold">
        {t("reviews.page.termDiscussions")}
      </h3>
      {terms.map((iri) => (
        <div key={iri} className="space-y-1">
          <Link className="app-action-link text-sm font-semibold" to={appPaths.ontology({ version: review.head, termIri: iri })} title={iri}>
            {compactIri(iri)}
          </Link>
          <DiscussionPanel
            projectId={review.changeSet.projectId}
            subject={{ kind: "ontology_term", value: iri }}
            changeSetId={review.changeSet.changeSetId}
            preloaded={discussion}
            onChanged={onChanged}
          />
        </div>
      ))}
    </section>
  );
}
